import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Clock, FileText, Library, Sparkles } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { DataCard } from '../components/common/DataCard';
import { StatsCard } from '../components/common/StatsCard';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import { useApp } from '../contexts/AppContext';
import api from '../services/api';

export function LearningPage() {
  const { currentUser } = useApp();
  const navigate = useNavigate();
  const isTeacher = currentUser?.role === 'teacher';
  const isStudent = currentUser?.role === 'student';

  // Teacher: calculate hours taught today from schedule
  const [hoursTaught, setHoursTaught] = useState(0);
  const [assignmentCount, setAssignmentCount] = useState(0);

  useEffect(() => {
    if (isTeacher) {
      loadTeacherData();
    }
  }, [isTeacher]);

  const loadTeacherData = async () => {
    try {
      const schedule = await api.getTeacherSchedule('today');
      // Count periods assigned today
      const today = new Date();
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const todayName = days[today.getDay()];
      const todayPeriods = (schedule?.timetable || []).filter(
        t => t.dayOfWeek === todayName && t.teacherId === currentUser?.userId
      );
      setHoursTaught(todayPeriods.length);
    } catch (e) {
      console.error(e);
    }
  };

  const title = isTeacher ? 'Teaching' : 'Learning';
  const description = isTeacher
    ? 'Manage assignments, courses, and teaching resources.'
    : 'Access your courses, assignments, and learning materials.';
  const moduleLabel = isTeacher ? 'Teaching Module' : 'Learning Module';

  return (
    <div data-testid="learning-page" className="space-y-8 animate-fade-in">
      <PageHeader title={title} description={description} />

      <div className={`grid grid-cols-1 sm:grid-cols-2 ${isTeacher ? 'lg:grid-cols-4' : 'lg:grid-cols-4'} gap-6 [&>*]:h-full`}>
        <StatsCard title="Courses" value="0" description="no courses yet" icon={BookOpen} delay={0} />
        
        {isTeacher ? (
          <StatsCard title="Hours Taught Today" value={hoursTaught} description="based on schedule" icon={Clock} delay={0.05} />
        ) : (
          <StatsCard title="Hours Learned" value="0" icon={Clock} delay={0.05} />
        )}
        
        {/* Teachers: Show ONLY Content Engine here (Assignments card removed per spec).
            Students: keep their existing 'Assignments' link. */}
        {isTeacher ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="cursor-pointer transition-transform hover:scale-[1.02] h-full"
                  onClick={() => navigate('/teacher-assignments')}
                  data-testid="card-content-engine"
                >
                  <StatsCard
                    title="Content Engine"
                    value="AI"
                    description="curriculum-aligned generation"
                    icon={Sparkles}
                    delay={0.1}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Click to open the AI Content Engine — generate lessons, PPTs, assessments</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="cursor-pointer transition-transform hover:scale-[1.02] h-full"
                  onClick={() => navigate('/student-assignments')}
                  data-testid="card-assignments"
                >
                  <StatsCard title="Assignments" value={assignmentCount} description="view assigned" icon={FileText} delay={0.1} />
                </div>
              </TooltipTrigger>
              <TooltipContent><p>Click to view your assigned tasks</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="cursor-pointer transition-transform hover:scale-[1.02] h-full"
                onClick={() => navigate('/library')}
                data-testid="card-resources"
              >
                <StatsCard title="Resources" value="Library" description="browse collection" icon={BookOpen} delay={0.15} />
              </div>
            </TooltipTrigger>
            <TooltipContent><p>Click to browse NCERT textbooks and Gutenberg classics</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <DataCard className="text-center py-16" delay={0.2}>
        <BookOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">{moduleLabel}</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          {isTeacher
            ? 'Create assignments, tests, and manage your teaching materials. Click on Assignments to open the AI Academic Creation Engine.'
            : 'The learning module is not yet configured for your school. Once courses and materials are added, they will appear here.'
          }
        </p>
      </DataCard>
    </div>
  );
}
